import { Link } from 'react-router-dom'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@server/router'
import { ScrollingEllipsisText } from '@/components/ScrollingEllipsisText'
import { trpc } from '@/lib/trpc'
import { filterDemoSkills } from '@/lib/demo-skills'

// s3:// URIs can't be loaded by the browser directly, so fall back to the
// name-based placeholder tile when that's all we have.
function isUsableThumbnail(url: string | null | undefined): url is string {
  if (!url) return false
  if (url.startsWith('s3://')) return false
  return true
}

function SkillsList() {
  const { data: skillsRaw = [], isLoading, error } = trpc.skills.list.useQuery()
  const skills = filterDemoSkills(skillsRaw)

  if (isLoading) {
    return <SkillsListSkeleton />
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <h2 className="text-h2 mb-2 text-text-primary">Failed to load skills</h2>
          <p className="text-body-default text-text-secondary">Please try again later</p>
        </div>
      </div>
    )
  }

  if (skills.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <h2 className="text-h2 mb-2 text-text-primary">No skills found</h2>
          <p className="text-body-default text-text-secondary">Check back later for new content</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {skills.map((skill) => (
        <SkillRow key={skill.id} skill={skill} />
      ))}
    </div>
  )
}

type SkillListItem = inferRouterOutputs<AppRouter>['skills']['list'][number]

function SkillRow({ skill }: { skill: SkillListItem }) {
  return (
    <Link
      to={`/skills/${skill.id}`}
      className="flex items-center gap-4 rounded-lg border border-border-default bg-surface-secondary p-4 transition-colors hover:bg-surface-tertiary"
    >
      <div className="relative h-[156px] w-[156px] flex-shrink-0 overflow-hidden rounded-[18px] bg-brand-navy">
        {isUsableThumbnail(skill.thumbnail) ? (
          <img
            src={skill.thumbnail}
            alt={skill.scope_name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-3">
            {/* One line: a long scope name ("WSIAnalysis") used to break mid-word.
                It ellipsizes instead, and scrolls across while the name is hovered. */}
            <ScrollingEllipsisText
              text={skill.scope_name}
              className="text-display-small w-full text-center capitalize text-white"
            />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <h2 className="text-h2 mb-1 capitalize text-text-primary">{skill.scope_name}</h2>
        <p className="text-body-default capitalize text-text-secondary">{skill.class_name}</p>
      </div>
    </Link>
  )
}

function SkillsListSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="flex animate-pulse items-center gap-4 rounded-lg border border-border-default bg-surface-secondary p-4"
        >
          <div className="h-[156px] w-[156px] flex-shrink-0 rounded-[18px] bg-surface-tertiary"></div>
          <div className="flex-1">
            <div className="mb-2 h-6 w-48 rounded bg-surface-tertiary"></div>
            <div className="h-4 w-32 rounded bg-surface-tertiary"></div>
          </div>
        </div>
      ))}
    </div>
  )
}

export function SkillsPage() {
  return (
    <main className="min-h-screen bg-surface-primary">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-h1 mb-8 text-text-primary">Skills</h1>
        <SkillsList />
      </div>
    </main>
  )
}
